import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  AssessmentSortBy,
  AssessmentSortById,
  ExaminerFunction,
  ExamTaskGroupSolutions,
  ExamTaskGroupSolutionsProgressData,
  Filters,
} from './Assessment.types';
import FilterStateGroup from '../../models/filterStateGroup.types';
import { IPageInfo } from '../../models/page.types';
import { AdvanceFilter, PointsFilterType } from './Modals/AdvancedFilter/AdvanceFilter.types';
import _ from 'lodash';
import { getFormattedDateRangeValue } from '../../utils/dateFunctionUtils';
import {
  Alerts,
  convertAssessmentQualityStateScoring,
  convertExamTaskGroupSolutionState,
  convertExamTaskGroupSolutionStateFromEnumId,
  getAssessmentQualityLabel,
  Statuses,
  Verifier,
} from './Helpers/functionsHelpers';
import { getErrorPageUrl } from '../../utils/routerUrls';
import { AxiosError, AxiosResponse } from 'axios';
import {
  getAssessmentSessionStateId,
  getExamTaskSolutionsForExaminer,
} from '../../services/exams/assessmentSession/assessments.service';
import { AssessmentSessionState, DashboardTile } from '../Dashboard/Dashboard.types';
import { ExaminersList } from '../../components/ExaminersStructure/ExaminersStructure.types';
import { useLayoutContext } from '../../Context/Layout.context';
import {
  AssessmentSessionProcessTypeId,
  ExamTaskGroupSolutionsResponse,
  ExamTaskGroupSolutionStatus,
  Status,
} from '../../services/exams/assessmentSession/assessments.types';
import { isExaminerBlockedInSession } from '../../services/exams/examiningTeam/examiningTeam.service';
import { User } from 'oidc-client';
import { getExamTaskGroupSolutionStatus } from '../../services/distribution/distribution.service';
import { RootState } from '../../store';
import {
  AssessmentDataInfo,
  DataInfoType,
  defaultAssessmentFilter,
} from '../../reducers/filtersReducer/filterSlice.types';
import { filtersActions } from '../../reducers/filtersReducer/filtersSlice'; // still used for filter updates, not for caching
import { AdvancedFilterBodyAssessment } from '../SampleAssessment/SampleAssessment.types';
import { getAssessmentTableFilterValues } from './assessmentFilters';

export const getDataInfoBasedOnProcess = (
  processTypeId: AssessmentSessionProcessTypeId,
):
  | DataInfoType.AssessmentControlInfo
  | DataInfoType.AssessmentTrainingInfo
  | DataInfoType.AssessmentScoringInfo
  | DataInfoType.AssessmentQualificationInfo => {
  if (processTypeId === AssessmentSessionProcessTypeId.Training) return DataInfoType.AssessmentTrainingInfo;
  if (processTypeId === AssessmentSessionProcessTypeId.Control) return DataInfoType.AssessmentControlInfo;
  if (processTypeId === AssessmentSessionProcessTypeId.Qualification) return DataInfoType.AssessmentQualificationInfo;
  if (processTypeId === AssessmentSessionProcessTypeId.Scoring) return DataInfoType.AssessmentScoringInfo;
  return DataInfoType.AssessmentTrainingInfo;
};

export interface UseAssessmentTableDataProps {
  assessmentSession: DashboardTile | null;
  examiner: ExaminersList | null | undefined;
  processTypeStringId: string;
  processTypeId: AssessmentSessionProcessTypeId;
  userFunction: ExaminerFunction;
  auth: User;
}

export const useAssessmentTableData = ({
  assessmentSession,
  examiner,
  processTypeStringId,
  processTypeId,
  userFunction,
  auth,
}: UseAssessmentTableDataProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { showLoader } = useLayoutContext();
  const dataInfoType = getDataInfoBasedOnProcess(processTypeId);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const assessmentsDataInfo = useSelector((state: RootState) => state.filters[dataInfoType]) as AssessmentDataInfo;

  const [isFirstView, setIsFirstView] = useState<boolean>(true);
  const [advancedFilterBody, setAdvancedFilterBody] = useState<AdvancedFilterBodyAssessment[]>(
    assessmentsDataInfo.filterData.advancedFilterBody,
  );
  const [examinerHasScoreToSendback, setExaminerHasScoreToSendback] = useState<boolean>(false);
  const [examTaskGroupSolutionsData, setExamTaskGroupSolutionsData] = useState<ExamTaskGroupSolutions[] | null>(null);
  const [advancedFilterModalOpen, setAdvancedFilterModalOpen] = useState<boolean>(false);

  const [isExaminerBlocked, setIsExaminerBlocked] = useState<boolean>(false);
  const [isEmptyList, setIsEmptyList] = useState<boolean>(false);
  const [assessmentSessionStateId, setAssessmentSessionStateId] = useState<AssessmentSessionState>();
  const [examTaskGroupSolutionStatus, setExamTaskGroupSolutionStatus] = useState<ExamTaskGroupSolutionStatus>();

  const [progressBarData, setProgressBarData] = useState<ExamTaskGroupSolutionsProgressData>({
    deskLimit: 0,
    totalLimit: 0,
    newTaskGroupSolutions: 0,
    openTaskGroupSolutions: 0,
    assessedTaskGroupSolutions: 0,
    closedTaskGroupSolutions: 0,
  });

  const [filter, setFilter] = useState<Filters>({
    ...assessmentsDataInfo.filterData.filter,
    verificationState: assessmentsDataInfo.filterData.filter.verificationState ?? [],
  });
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [assessmentQualityFilter, setAssessmentQualityFilter] = useState<string[]>([]);
  const [alertFilter, setAlertFilter] = useState<string[]>([]);
  const [verifierFilter, setVerifierFilter] = useState<string[]>([]);
  const [isValidFilter, setIsValidFilter] = useState<boolean>(true);
  const [filterState, setFilterState] = useState<FilterStateGroup[]>([]);

  const [advancedFilter, setAdvancedFilter] = useState<AdvanceFilter | null>(
    assessmentsDataInfo.filterData.advancedFilter,
  );
  const [isResetAdvancedFilter, setIsResetAdvancedFilter] = useState<boolean>(false);
  const [checkedTasks, setCheckedTasks] = useState<string[]>(
    assessmentsDataInfo.filterData.advancedFilterBody.map((x) => x.id),
  );
  const [checkedCriterions, setCheckedCriterions] = useState<string[]>(
    assessmentsDataInfo.filterData.advancedFilterBody.map((x) => x.id),
  );

  const [sortByColumnId, setSortByColumnId] = useState<AssessmentSortBy>(
    assessmentsDataInfo.filterData.sort.sortByColumnId,
  );
  const [isDescending, setIsDescending] = useState<boolean>(assessmentsDataInfo.filterData.sort.isDescending);

  const latestRequestRef = useRef<{
    examinerId: string | undefined;
    processTypeId: AssessmentSessionProcessTypeId;
  } | null>(null);

  const handleCurrentPage = (response: IPageInfo) => {
    if (response.totalCount < assessmentsDataInfo.filterData.pageInfo.pageSize) {
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            pageInfo: {
              ...response,
              currentPage: 0,
            },
          },
        }),
      );
    } else {
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            pageInfo: {
              ...response,
            },
          },
        }),
      );
    }
  };

  const handleFilterChange = useCallback(
    (name: string, value: string | string[]) => {
      typeof value === 'string' && name !== 'pointsFrom' && name !== 'pointsTo' && value.length === 1
        ? setIsValidFilter(false)
        : setIsValidFilter(true);
      if (filter[name] === value) return;

      setFilter((state) => {
        const examTaskGroupSolutionScoreId = searchParams?.get('examTaskGroupSolutionScoreId');
        const examinerName = searchParams?.get('examinerName');
        if (state[name] !== value) {
          if ((examTaskGroupSolutionScoreId === null && examinerName === null) || value === '') {
            return {
              ...state,
              [name]: value,
            };
          } else {
            if (name === 'examTaskGroupSolutionScoreId' && value !== '') {
              return {
                ...state,
                [name]: examTaskGroupSolutionScoreId ?? '',
              };
            }
            if (name === 'examinerName' && value !== '') {
              return {
                ...state,
                [name]: examinerName ?? '',
              };
            }
            return {
              ...state,
              [name]: value,
            };
          }
        } else {
          return state;
        }
      });
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            filter: {
              [name]: value,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );

      const newValue = value;
      if (name === 'alerts') {
        setAlertFilter([...newValue]);
      }
      if (name === 'verificationState') {
        setVerifierFilter([...(newValue as string[])]);
      }
    },
    [setFilter, alertFilter, verifierFilter, filter],
  );

  const handleNoAnswerFilterChange = () => {
    setFilter((state) => {
      return {
        ...state,
        examTaskNumbersWithNoAnswer: state.examTaskNumbersWithNoAnswer !== 'true' ? 'true' : '',
      };
    });
    dispatch(
      filtersActions.updateFilter({
        filterType: dataInfoType,
        newValues: {
          filter: {
            examTaskNumbersWithNoAnswer:
              assessmentsDataInfo.filterData.filter.examTaskNumbersWithNoAnswer !== 'true' ? 'true' : '',
          },
        },
      }),
    );
  };

  useEffect(() => {
    if (searchParams) {
      const examTaskGroupSolutionScoreId = searchParams?.get('examTaskGroupSolutionScoreId');
      const examinerName = searchParams?.get('examinerName');
      if (examTaskGroupSolutionScoreId) {
        handleFilterChange('examTaskGroupSolutionScoreId', examTaskGroupSolutionScoreId);
      }
      if (examinerName) {
        handleFilterChange('examinerName', examinerName);
      }
    }
  }, [searchParams]);

  const disableChangesTillResponse = () => {
    dispatch(
      filtersActions.updateFilter({
        filterType: dataInfoType,
        newValues: {
          pageInfo: {
            hasNext: false,
            hasPrevious: false,
          },
        },
      }),
    );
  };

  const getTableFilterValues = () => {
    return getAssessmentTableFilterValues(filter);
  };

  const getSortByPropertyNameFromColumnId = (columId: AssessmentSortBy) => {
    switch (columId) {
      case 'examTaskGroupSolutionScoreId':
        return AssessmentSortById.ExamTaskGroupSolutionScoreId;
      case 'examinerName':
        return AssessmentSortById.ExaminerName;
      case 'pointsSum':
        return AssessmentSortById.Points;
      case 'progress':
        return AssessmentSortById.Progress;
      case 'downloadDate':
        return AssessmentSortById.DownloadDate;
      case 'updateDate':
        return AssessmentSortById.UploadDate;
      default:
        return AssessmentSortById.DownloadDate;
    }
  };

  const refreshData = _.debounce((processBarData?: boolean, signal?: AbortSignal) => {
    if (!examiner || !assessmentSession) return;
    if (!isValidFilter) return;
    latestRequestRef.current = { examinerId: examiner.id, processTypeId };
    const filters = getTableFilterValues();
    showLoader(true);
    disableChangesTillResponse();
    getExamTaskSolutionsForExaminer(
      assessmentSession.assessmentSessionId,
      examiner.id,
      examiner.function.toString(),
      processTypeStringId,
      signal,
      processBarData,
      assessmentsDataInfo.filterData.pageInfo.pageSize,
      assessmentsDataInfo.filterData.pageInfo.currentPage,
      getSortByPropertyNameFromColumnId(sortByColumnId),
      isDescending,
      advancedFilterBody,
      ...(filters as string[]),
    )
      .then((res: AxiosResponse) => {
        if (
          latestRequestRef.current &&
          latestRequestRef.current.examinerId === examiner.id &&
          latestRequestRef.current.processTypeId === processTypeId
        ) {
          if (examiner && examiner.examiner && res.data.examinersHaveScoreToSendBack) {
            setExaminerHasScoreToSendback(!!res.data.examinersHaveScoreToSendBack[examiner.examiner.id]);
          }
          setExamTaskGroupSolutionsData(res.data.list);
          handleCurrentPage(res.data.pageInfo);
          showLoader(false);
          setIsFirstView(false);
          if (res.data.progress) {
            setProgressBarData(res.data.progress);
          }
          if (advancedFilterBody) {
            setAdvancedFilterModalOpen(false);
          }
        }
      })
      .catch((e: AxiosError) => {
        console.warn({ e });
        if (e?.message !== 'canceled') {
          history.push(getErrorPageUrl(e?.code ?? 500, 'examiner'));
        }
      });
  }, 1000);

  useEffect(() => {
    const state: FilterStateGroup[] = [];
    if (!filter) return;
    if (Object.values(filter).some((e) => e.length !== 0)) {
      state.push({
        name: '',
        checkedItems: [],
      });
    }
    if (filter.flag.length > 0) {
      state.push({
        name: 'Flaga:',
        checkedItems: [...filter.flag],
      });
    }
    if (filter.status.length > 0) {
      state.push({
        name: 'Status:',
        checkedItems: [...filter.status].map((item) => Statuses.filter((e) => e.id === item)[0].label),
      });
    }
    if (filter.pointsFrom.length > 0) {
      state.push({
        name: 'Suma pkt. od:',
        checkedItems: [[...filter.pointsFrom].join('').toString()],
      });
    }
    if (filter.pointsTo.length > 0) {
      state.push({
        name: 'Suma pkt. do:',
        checkedItems: [[...filter.pointsTo].join('').toString()],
      });
    }
    if (
      filter.examTaskNumbersWithNoAnswer.length > 0 &&
      !(filter.assessmentQualityId.length && filter.assessmentQualityId.length > 0)
    ) {
      state.push({
        name: 'Suma pkt.:',
        checkedItems: [...['Brak odpowiedzi']],
      });
    }
    if (filter.assessmentQualityId.length && filter.assessmentQualityId.length > 0) {
      if (filter.examTaskNumbersWithNoAnswer.length > 0) {
        state.filter((e) => e.name !== 'Suma pkt.:');
      }
      state.push({
        name: 'Suma pkt.:',
        checkedItems:
          filter.examTaskNumbersWithNoAnswer.length > 0
            ? ['Brak odpowiedzi'].concat(
                [...filter.assessmentQualityId].map((item) => getAssessmentQualityLabel(item, processTypeId)),
              )
            : [...filter.assessmentQualityId].map((item) => getAssessmentQualityLabel(item, processTypeId)),
      });
    }
    if (filter.progress.length > 0) {
      state.push({
        name: 'Postęp:',
        checkedItems: [...filter.progress].map((item) => {
          return `${item}%`;
        }),
      });
    }
    if (filter.alerts.length > 0) {
      state.push({
        name: 'Monit:',
        checkedItems: [...filter.alerts].map((item) => Alerts.filter((e) => e.id === item)[0].label),
      });
    }
    if (filter.verificationState.length > 0) {
      state.push({
        name: 'Weryfikator:',
        checkedItems: [...filter.verificationState].map((item) => Verifier.filter((e) => e.id === item)[0]?.label),
      });
    }
    if (filter.downloadDateFrom.length > 0) {
      state.push({
        name: 'Pobrane:',
        checkedItems: [
          getFormattedDateRangeValue(filter.downloadDateFrom.toString(), filter.downloadDateTo.toString()),
        ],
      });
    }
    if (filter.updateDateFrom.length > 0) {
      state.push({
        name: 'Aktualizacja:',
        checkedItems: [getFormattedDateRangeValue(filter.updateDateFrom.toString(), filter.updateDateTo.toString())],
      });
    }
    setFilterState(state);
  }, [filter]);

  const refreshStatusOfSentbackAssessedExamTaskGroupSolutionScores = (
    examTaskGroupSolutionScoreIds: string[],
    newStatus: Status,
  ) => {
    if (examTaskGroupSolutionsData && examTaskGroupSolutionsData.length > 0) {
      const data = [...examTaskGroupSolutionsData];
      examTaskGroupSolutionScoreIds.forEach((id: string) => {
        const sentbackItemIndex = examTaskGroupSolutionsData.findIndex((item: ExamTaskGroupSolutions) => {
          return item.examTaskGroupSolutionScoreId === id;
        });
        const sentbackItem = {
          ...examTaskGroupSolutionsData[sentbackItemIndex],
        };
        sentbackItem.status = newStatus;
        data[sentbackItemIndex] = sentbackItem;
      });
      setExamTaskGroupSolutionsData(data);
    } else {
      //erronous situation when examTaskGroupSolutionsData have been cleared...
      refreshData(true);
    }
  };

  const setExceptionalSendbackCaseFailure = (examTaskGroupSolutionScoreId: string[]) => {
    if (examTaskGroupSolutionsData && examTaskGroupSolutionsData.length > 0) {
      const data = [...examTaskGroupSolutionsData];
      examTaskGroupSolutionScoreId.forEach((id: string) => {
        const sentbackItemIndex = examTaskGroupSolutionsData.findIndex((item: ExamTaskGroupSolutions) => {
          return item.examTaskGroupSolutionScoreId === id;
        });
        const sentbackItem = {
          ...examTaskGroupSolutionsData[sentbackItemIndex],
        };
        sentbackItem.isSendbackResultFailedExceptionalCase = true;
        data[sentbackItemIndex] = sentbackItem;
      });
      setExamTaskGroupSolutionsData(data);
    }
  };

  const handleAssessmentQualityFilterChange = useCallback(
    (value?: string | string[]) => {
      if (value && typeof value === 'string') {
        const val = assessmentQualityFilter.some((f) => f === value)
          ? assessmentQualityFilter.filter((f) => f !== value)
          : [...assessmentQualityFilter, value];
        setAssessmentQualityFilter(val);
        handleFilterChange('assessmentQualityId', val);
      } else {
        setAssessmentQualityFilter([]);
        handleFilterChange('assessmentQualityId', []);
        setFilter((state) => {
          return {
            ...state,
            examTaskNumbersWithNoAnswer: '',
          };
        });
        dispatch(
          filtersActions.updateFilter({
            filterType: dataInfoType,
            newValues: {
              filter: {
                examTaskNumbersWithNoAnswer: '',
              },
              pageInfo: {
                currentPage: 0,
              },
            },
          }),
        );
      }
    },
    [assessmentQualityFilter],
  );

  const handleStatusFilterChange = useCallback(
    (value?: string) => {
      if (value && typeof value === 'string') {
        const val = statusFilter.some((f) => f === value)
          ? statusFilter.filter((f) => f !== value)
          : [...statusFilter, value];

        const convertedValue: string[] = val.map((status) => convertExamTaskGroupSolutionState(status));
        setStatusFilter(val);
        handleFilterChange('status', convertedValue);
      } else {
        setStatusFilter([]);
        handleFilterChange('status', []);
      }
    },
    [statusFilter],
  );

  const getAssessment = useCallback(
    (_page?: number, _newPageSize?: number) => {
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            pageInfo: {
              currentPage: _page ? _page : 0,
              pageSize: _newPageSize ? _newPageSize : assessmentsDataInfo.filterData.pageInfo.pageSize,
            },
          },
        }),
      );
    },
    [assessmentsDataInfo.filterData.pageInfo],
  );

  const handleAdvancedFilterChange = (filter: AdvanceFilter | null) => {
    setAdvancedFilter(filter);
    dispatch(
      filtersActions.updateFilter({
        filterType: dataInfoType,
        newValues: {
          advancedFilter: filter,
          pageInfo: {
            currentPage: 0,
          },
        },
      }),
    );
    if (filter) {
      const filterCopy = JSON.parse(JSON.stringify(filter));
      const tasksPoints: any[] = [];
      filterCopy?.examTasks.map((task: any) => {
        if (checkedTasks.includes(task.id)) {
          tasksPoints.push({
            id: task.id,
            exactPoints: task.pointsFilterType === PointsFilterType.exactPonts ? parseInt(task.exactPoints) : null,
            pointsFrom: task.pointsFilterType === PointsFilterType.rangePonts ? parseInt(task.pointsFrom) : null,
            pointsTo: task.pointsFilterType === PointsFilterType.rangePonts ? parseInt(task.pointsTo) : null,
            noAnswer:
              task.noAnswer === 0 || task.pointsFilterType === PointsFilterType.rangePonts || task.exactPoints
                ? null
                : task.noAnswer === 1
                ? true
                : false,
            examScoringSchemas: [],
          });
          task.examScoringSchemas.map((criterion: any) => {
            if (checkedCriterions.includes(criterion.id)) {
              tasksPoints[tasksPoints.length - 1].examScoringSchemas.push({
                id: criterion.id,
                exactPoints:
                  criterion.pointsFilterType === PointsFilterType.exactPonts ? parseInt(criterion.exactPoints) : null,
                pointsFrom:
                  criterion.pointsFilterType === PointsFilterType.rangePonts ? parseInt(criterion.pointsFrom) : null,
                pointsTo:
                  criterion.pointsFilterType === PointsFilterType.rangePonts ? parseInt(criterion.pointsTo) : null,
              });
            }
          });
        }
      });
      setAdvancedFilterBody(tasksPoints);
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            advancedFilterBody: tasksPoints,
          },
        }),
      );
    }
  };

  const uncheckAllFilters = () => {
    setFilterState([]);
    setStatusFilter([]);
    setAssessmentQualityFilter([]);
    setAlertFilter([]);
    setFilter(defaultAssessmentFilter);
    dispatch(filtersActions.resetFilter(dataInfoType));
  };

  const handleUncheckFlagItem = useCallback(
    (value: string) => {
      const flagFilter = [...(filter.flag as string[])];
      const val = flagFilter.some((f) => f === value) ? flagFilter.filter((f) => f !== value) : [...filter.flag, value];
      setFilter((state) => ({
        ...state,
        flag: val,
      }));
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            filter: {
              flag: val,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
    },
    [filter],
  );

  const handleUncheckStatusItem = useCallback(
    (value: string) => {
      const val = statusFilter.some((f) => f === value)
        ? statusFilter.filter((f) => f !== value)
        : [...statusFilter, value];
      setStatusFilter(val);
      setFilter((state) => ({
        ...state,
        status: [...val].map((item) => Statuses.filter((e) => e.label === item)[0].id),
      }));
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            filter: {
              status: [...val].map((item) => Statuses.filter((e) => e.label === item)[0].id),
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
    },
    [statusFilter],
  );

  const handleUncheckPointsItem = useCallback(
    (value: string) => {
      if (value === 'Brak odpowiedzi') {
        setFilter((state) => ({
          ...state,
          examTaskNumbersWithNoAnswer: '',
        }));
        dispatch(
          filtersActions.updateFilter({
            filterType: dataInfoType,
            newValues: {
              filter: {
                examTaskNumbersWithNoAnswer: '',
              },
              pageInfo: {
                currentPage: 0,
              },
            },
          }),
        );
      } else {
        const assessmentQualityIdFilter = [...filter.assessmentQualityId];
        const valueId = convertAssessmentQualityStateScoring(value);
        const val = assessmentQualityIdFilter.some((f) => f === valueId)
          ? assessmentQualityIdFilter.filter((f) => f !== valueId)
          : [...assessmentQualityIdFilter, valueId];
        setFilter((state) => ({
          ...state,
          assessmentQualityId: val,
        }));
        setAssessmentQualityFilter(val);
        dispatch(
          filtersActions.updateFilter({
            filterType: dataInfoType,
            newValues: {
              filter: {
                assessmentQualityId: val,
              },
              pageInfo: {
                currentPage: 0,
              },
            },
          }),
        );
      }
    },
    [filter],
  );

  const handleUncheckProgresItem = useCallback(
    (value: string) => {
      const rawValue = value.slice(0, value.length - 1);
      const progressFilter = [...filter.progress];
      const val = progressFilter.some((f) => f === rawValue)
        ? progressFilter.filter((f) => f !== rawValue)
        : [...progressFilter, rawValue];
      setFilter((state) => ({
        ...state,
        progress: val,
      }));
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            filter: {
              progress: val,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
    },
    [filter],
  );

  const handleUncheckAlertsItem = useCallback(
    (value: string) => {
      const alertsFilter = [...filter.alerts];
      const valueId = Alerts.filter((e) => e.label === value)[0].id;
      const val = alertsFilter.some((f) => f === valueId)
        ? alertsFilter.filter((f) => f !== valueId)
        : [...alertsFilter, valueId];
      setAlertFilter(val);
      setFilter((state) => ({
        ...state,
        alerts: val,
      }));
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            filter: {
              alerts: val,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
    },
    [filter],
  );

  const handleUncheckVerifierItem = useCallback(
    (value: string) => {
      const verifierCurrent = [...filter.verificationState] as string[];
      const valueId = Verifier.filter((e) => e.label === value)[0].id;
      const SUBORDINATE_IDS = ['0', '1', '2', '3'];
      const isRemovingParent = valueId === '5';

      let val = verifierCurrent.some((f) => f === valueId)
        ? verifierCurrent.filter((f) => f !== valueId)
        : [...verifierCurrent, valueId];

      if (isRemovingParent) {
        val = val.filter((f) => !SUBORDINATE_IDS.includes(f));
      }

      setVerifierFilter(val);
      setFilter((state) => ({
        ...state,
        verificationState: val,
      }));
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            filter: {
              verificationState: val,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
    },
    [filter],
  );

  const uncheckFilterItem = (item?: string) => {
    if (item) {
      const element = filterState.find((e) => e.checkedItems.find((x) => x === item));
      if (element?.name) {
        switch (element.name) {
          case 'Flaga:':
            handleUncheckFlagItem(item);
            break;
          case 'Status:':
            handleUncheckStatusItem(item);
            break;
          case 'Suma pkt.:':
            handleUncheckPointsItem(item);
            break;
          case 'Suma pkt. od:':
            {
              setFilter((state) => ({
                ...state,
                pointsFrom: '',
              }));
              dispatch(
                filtersActions.updateFilter({
                  filterType: dataInfoType,
                  newValues: {
                    filter: {
                      pointsFrom: '',
                    },
                    pageInfo: {
                      currentPage: 0,
                    },
                  },
                }),
              );
            }
            break;
          case 'Suma pkt. do:':
            {
              setFilter((state) => ({
                ...state,
                pointsTo: '',
              }));
              dispatch(
                filtersActions.updateFilter({
                  filterType: dataInfoType,
                  newValues: {
                    filter: {
                      pointsTo: '',
                    },
                    pageInfo: {
                      currentPage: 0,
                    },
                  },
                }),
              );
            }
            break;
          case 'Postęp:':
            handleUncheckProgresItem(item);
            break;
          case 'Monit:':
            handleUncheckAlertsItem(item);
            break;
          case 'Weryfikator:':
            handleUncheckVerifierItem(item);
            break;
          case 'Pobrane:':
            {
              setFilter((state) => ({
                ...state,
                downloadDateFrom: '',
                downloadDateTo: '',
              }));
              dispatch(
                filtersActions.updateFilter({
                  filterType: dataInfoType,
                  newValues: {
                    filter: {
                      downloadDateFrom: '',
                      downloadDateTo: '',
                    },
                    pageInfo: {
                      currentPage: 0,
                    },
                  },
                }),
              );
            }
            break;
          case 'Aktualizacja:': {
            setFilter((state) => ({
              ...state,
              updateDateFrom: '',
              updateDateTo: '',
            }));
            dispatch(
              filtersActions.updateFilter({
                filterType: dataInfoType,
                newValues: {
                  filter: {
                    updateDateFrom: '',
                    updateDateTo: '',
                  },
                  pageInfo: {
                    currentPage: 0,
                  },
                },
              }),
            );
          }
        }
      }
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;
    setExamTaskGroupSolutionsData(null);
    if (!examiner) return;
    if (!auth.scope.includes('assessmentSession') || userFunction === undefined) {
      return;
    }
    const isPZEViewingVerifier =
      userFunction === ExaminerFunction.PZE && !!examiner?.examiner?.examinerPermission?.isVerifier;
    if (isFirstView) {
      if (userFunction === ExaminerFunction.CZE) {
        isExaminerBlocked === false &&
          isExaminerBlockedInSession().then((result: boolean) => setIsExaminerBlocked(result));
        if (processTypeId !== undefined) {
          setIsFirstView(false);
          setIsEmptyList(true);
          if (
            !(
              processTypeId === AssessmentSessionProcessTypeId.Scoring &&
              examiner?.examiner?.examinerPermission?.isVerifier === true
            )
          )
            getExamTaskGroupSolutionStatus(processTypeId)
              .then((res: AxiosResponse<ExamTaskGroupSolutionStatus>) => {
                setExamTaskGroupSolutionStatus(res.data);
              })
              .catch((e: AxiosResponse) => {
                console.log({ e });
              });
        }
      }
      getAssessmentSessionStateId().then((res: AxiosResponse) => {
        setAssessmentSessionStateId(res.data);
      });
      refreshData(!(isPZEViewingVerifier && processTypeId === AssessmentSessionProcessTypeId.Scoring), signal);
      setIsEmptyList(true);
    } else {
      isExaminerBlockedInSession().then((result: boolean) => setIsExaminerBlocked(result));
      refreshData(!(isPZEViewingVerifier && processTypeId === AssessmentSessionProcessTypeId.Scoring), signal);
      setIsEmptyList(false);
    }
    return () => {
      abortController.abort();
    };
  }, [
    examiner?.id,
    processTypeId,
    assessmentsDataInfo.filterData.pageInfo.currentPage,
    assessmentsDataInfo.filterData.pageInfo.pageSize,
    filter,
    advancedFilterBody,
    auth.scope,
    userFunction,
    isDescending,
    sortByColumnId,
  ]);

  const onAdvancedFilterReset = () => {
    setIsResetAdvancedFilter(true);
    setAdvancedFilter(null);
    setCheckedCriterions([]);
    setCheckedTasks([]);
    setAdvancedFilterBody([]);
    dispatch(
      filtersActions.updateFilter({
        filterType: dataInfoType,
        newValues: {
          advancedFilter: null,
          advancedFilterBody: [],
        },
      }),
    );
  };

  const setIsDescendingOrder = (order: boolean) => {
    setIsDescending(order);
    dispatch(filtersActions.updateFilter({ filterType: dataInfoType, newValues: { sort: { isDescending: order } } }));
  };

  const setSortByColumn = (column: AssessmentSortBy) => {
    setSortByColumnId(column);
    dispatch(
      filtersActions.updateFilter({
        filterType: dataInfoType,
        newValues: { sort: { sortByColumnId: column } },
      }),
    );
  };

  const checkExamTaskGroupSolutionStatus = () => {
    if (processTypeId !== undefined) {
      if (
        processTypeId === AssessmentSessionProcessTypeId.Scoring &&
        examiner?.examiner?.examinerPermission?.isVerifier === true
      )
        return;
      getExamTaskGroupSolutionStatus(processTypeId)
        .then((res: AxiosResponse<ExamTaskGroupSolutionStatus>) => {
          setExamTaskGroupSolutionStatus(res.data);
        })
        .catch((e: AxiosResponse) => {
          console.log({ e });
        });
    }
  };

  return {
    filter,
    statusFilter,
    assessmentQualityFilter,
    filterState,
    pageInfo: assessmentsDataInfo.filterData.pageInfo,
    advancedFilter,
    setAdvancedFilter,
    isResetAdvancedFilter,
    setIsResetAdvancedFilter,
    checkedTasks,
    setCheckedTasks,
    checkedCriterions,
    setCheckedCriterions,
    sortByColumnId,
    setSortByColumnId: setSortByColumn,
    isDescending,
    setIsDescending: setIsDescendingOrder,
    handleFilterChange,
    handleNoAnswerFilterChange,
    refreshData,
    examTaskGroupSolutionsData,
    refreshStatusOfSentbackAssessedExamTaskGroupSolutionScores,
    setExceptionalSendbackCaseFailure,
    setAdvancedFilterModalOpen,
    progressBarData,
    examinerHasScoreToSendback,
    handleStatusFilterChange,
    handleAssessmentQualityFilterChange,
    uncheckFilterItem,
    uncheckAllFilters,
    getAssessment,
    advancedFilterModalOpen,
    handleAdvancedFilterChange,
    setIsExaminerBlocked,
    setIsEmptyList,
    isExaminerBlocked,
    isEmptyList,
    setExamTaskGroupSolutionStatus,
    examTaskGroupSolutionStatus,
    assessmentSessionStateId,
    onAdvancedFilterReset,
    checkExamTaskGroupSolutionStatus,
  };
};
